/**
 * WebSocket connection management hook
 */
import { useCallback, useEffect, useRef } from "react";

import debounce from "lodash/debounce";

import type { AppDispatch } from "app/store";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { stripAuthScheme } from "utils/auth-header.util";
import { setFavicon } from "utils/set-favicon.util";

import { createLogger } from "../../logging.util";
import {
  ERROR_MESSAGES,
  FAVICON_PATHS,
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_PAUSED
} from "../../medical-scribe-constants";
import type { TranscriptEntry } from "../../medical-scribe.slice";
import {
  pauseScribeRecording,
  resumeScribeRecording,
  setShowSilenceWarningDialog,
  setStreamingAuthenticated,
  setStreamingConnecting,
  setStreamingError,
  setStreamingPaused,
  setStreamingReconnecting,
  setStreamingScribeId,
  setStreamingSessionNumber,
  setStreamingTranscript,
  setStreamingWasResumed,
  startScribeRecording
} from "../../medical-scribe.slice";
import { updateRecordingSessionState } from "../../recording-session.util";
import {
  EXISTING_SOCKET_CLOSE_TIMEOUT_MS,
  MAX_RECONNECT_ATTEMPTS,
  MAX_WS_CONNECT_ATTEMPTS,
  WS_CONNECTION_TIMEOUT_MS,
  WS_RETRY_BASE_DELAY_MS,
  getWebSocketUrl
} from "../constants";
import {
  shouldDispatchInterimTranscript,
  shouldSkipAutoReconnect
} from "../resume-guards.util";
import type {
  MicrophoneInput,
  NormalizedTranscriptChunk,
  RecordingStateRefs,
  StartRecordingInternalOptions,
  WebSocketRefs
} from "../types";
import { mapChunksToTranscriptEntries, wait } from "../utils";
import type { FinalizePauseOptions } from "./use-recording-control";

interface UseWebSocketConnectionProps {
  dispatch: AppDispatch;
  token: string | undefined;
  wsRefs: WebSocketRefs;
  stateRefs: RecordingStateRefs;
  transcriptRef: React.MutableRefObject<TranscriptEntry[]>;
  lastInterimRef: React.MutableRefObject<TranscriptEntry | null>;
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>;
  audioWorkerRef: React.MutableRefObject<any>;
  audioRecordingIdRef: React.MutableRefObject<string | null>;
  startRecordingInternalRef: React.MutableRefObject<
    | ((
        patientId: number,
        chartNoteId: number | undefined,
        options?: StartRecordingInternalOptions
      ) => Promise<boolean>)
    | null
  >;
  microphoneFingerprintRef: React.MutableRefObject<MicrophoneInput | undefined>;
  cleanupResources: () => void | Promise<void>;
  startVolumeAnalysis: () => void;
  armPhantomSessionWatchdog: () => void;
  startChunkSaving: () => void;
  stopChunkSaving: () => Promise<true>;
  onTranscriptionReceived?: (text: string, isFinal: boolean) => void;
  finalizePauseRef?: React.MutableRefObject<
    ((options?: FinalizePauseOptions) => Promise<void>) | null
  >;
  closeWebSocketRef?: React.MutableRefObject<
    ((reason: string) => Promise<void>) | null
  >;
  resumeInFlightRef?: React.MutableRefObject<boolean>;
}

export const useWebSocketConnection = ({
  dispatch,
  token,
  wsRefs,
  stateRefs,
  transcriptRef,
  lastInterimRef,
  mediaRecorderRef,
  audioWorkerRef,
  audioRecordingIdRef,
  startRecordingInternalRef,
  microphoneFingerprintRef,
  cleanupResources,
  startVolumeAnalysis,
  armPhantomSessionWatchdog,
  startChunkSaving,
  stopChunkSaving,
  onTranscriptionReceived,
  finalizePauseRef,
  closeWebSocketRef,
  resumeInFlightRef
}: UseWebSocketConnectionProps) => {
  // Track if authentication was sent for the current WebSocket connection
  const authSentRef = useRef<boolean>(false);
  // Track if authentication already succeeded (response received)
  const authSucceededRef = useRef<boolean>(false);
  // Store authentication promise resolver to handle race conditions
  // If authentication response comes before authenticate() is called, we can resolve it
  const authResolverRef = useRef<{
    resolve: () => void;
    reject: (error: Error) => void;
  } | null>(null);

  // Ref-based callback to avoid invalidating handleTranscriptionMessage's useCallback deps
  const onTranscriptionReceivedRef = useRef(onTranscriptionReceived);
  useEffect(() => {
    onTranscriptionReceivedRef.current = onTranscriptionReceived;
  }, [onTranscriptionReceived]);

  const finalTranscriptEntriesRef = useRef<TranscriptEntry[]>([]);

  const debouncedInterimDispatchRef = useRef(
    debounce((entries: TranscriptEntry[]) => {
      if (!shouldDispatchInterimTranscript(stateRefs.isRecording.current)) {
        return;
      }
      dispatch(setStreamingTranscript(entries));
    }, 150)
  );

  useEffect(() => {
    const debounced = debouncedInterimDispatchRef.current;
    return () => {
      debounced.cancel();
    };
  }, []);

  /**
   * Handle transcription messages from WebSocket
   */
  const handleTranscriptionMessage = useCallback(
    (msg: NormalizedTranscriptChunk) => {
      const channel = msg?.channel;
      const alternatives = channel?.alternatives;

      if (alternatives && alternatives.length > 0) {
        const alternative = alternatives[0];
        const transcriptPart = alternative?.transcript;
        const isFinal = msg.is_final || false;

        // Extract speaker information from words
        let speaker: number | undefined;
        if (alternative?.words && alternative.words.length > 0) {
          speaker = alternative.words[0].speaker;
        }

        if (speaker === undefined) {
          speaker = alternative?.speaker;
        }

        if (speaker === undefined) {
          speaker = 0;
        }

        if (transcriptPart && transcriptPart.trim()) {
          const newEntry: TranscriptEntry = {
            speaker,
            text: transcriptPart.trim(),
            isFinal,
            timestamp: Date.now()
          };

          createLogger.log(
            `📝 Transcription [${isFinal ? "FINAL" : "interim"}] (speaker ${speaker}): "${transcriptPart.trim()}"`,
            stateRefs.recordingStartTime.current
          );

          if (isFinal) {
            debouncedInterimDispatchRef.current.cancel();
            finalTranscriptEntriesRef.current = [
              ...finalTranscriptEntriesRef.current,
              newEntry
            ];
            lastInterimRef.current = null;
            dispatch(
              setStreamingTranscript([...finalTranscriptEntriesRef.current])
            );
          } else {
            lastInterimRef.current = newEntry;
            debouncedInterimDispatchRef.current([
              ...finalTranscriptEntriesRef.current,
              newEntry
            ]);
          }

          onTranscriptionReceivedRef.current?.(transcriptPart.trim(), isFinal);
        }
      }
    },
    [dispatch, lastInterimRef, stateRefs]
  );

  /**
   * Ensure existing WebSocket is closed before creating new one
   */
  const ensureWebSocketClosed = useCallback(async () => {
    const existing = wsRefs.ws.current;
    if (!existing) {
      return;
    }

    if (existing.readyState === WebSocket.CLOSED) {
      if (wsRefs.ws.current === existing) {
        wsRefs.ws.current = null;
      }
      return;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      const finalize = () => {
        if (settled) {
          return;
        }
        settled = true;
        existing.removeEventListener("close", handleClose);
        resolve();
      };

      const timeoutId = globalThis.setTimeout(() => {
        createLogger.warn(
          "⚠️ Timed out waiting for previous WebSocket to close. Continuing...",
          stateRefs.recordingStartTime.current
        );
        finalize();
      }, EXISTING_SOCKET_CLOSE_TIMEOUT_MS);

      const handleClose = () => {
        globalThis.clearTimeout(timeoutId);
        finalize();
      };

      existing.addEventListener("close", handleClose);
      try {
        existing.close();
      } catch (err) {
        createLogger.error(
          "⚠️ Error closing stale WebSocket",
          err,
          stateRefs.recordingStartTime.current
        );
        globalThis.clearTimeout(timeoutId);
        finalize();
      }
    });

    if (wsRefs.ws.current === existing) {
      wsRefs.ws.current = null;
    }
  }, [wsRefs, stateRefs]);

  /**
   * Create WebSocket connection with timeout and handlers
   */
  const createWebSocketConnection = useCallback((): Promise<WebSocket> => {
    return new Promise((resolve, reject) => {
      const wsUrl = getWebSocketUrl();
      createLogger.log(
        `🔌 Connecting to WebSocket: ${wsUrl}`,
        stateRefs.recordingStartTime.current
      );
      const ws = new WebSocket(wsUrl);
      wsRefs.ws.current = ws;
      let settled = false;
      // Reset auth flags and resolver for new connection
      authSentRef.current = false;
      authSucceededRef.current = false;
      authResolverRef.current = null;

      const timeoutId = globalThis.setTimeout(() => {
        createLogger.error(
          `❌ WebSocket connection timed out after ${WS_CONNECTION_TIMEOUT_MS}ms`,
          undefined,
          stateRefs.recordingStartTime.current
        );
        if (!settled) {
          settled = true;
          if (wsRefs.ws.current === ws) {
            wsRefs.ws.current = null;
          }
          try {
            ws.close(4000, "Connection timeout");
          } catch (err) {
            createLogger.error(
              "⚠️ Error closing timed-out WebSocket",
              err,
              stateRefs.recordingStartTime.current
            );
          }
          reject(
            new Error(
              "Timed out establishing WebSocket connection. Please try again."
            )
          );
        }
      }, WS_CONNECTION_TIMEOUT_MS);

      const clearConnectionTimeout = () => {
        globalThis.clearTimeout(timeoutId);
      };

      ws.onopen = () => {
        clearConnectionTimeout();
        settled = true;
        createLogger.log(
          "✅ WebSocket connected",
          stateRefs.recordingStartTime.current
        );

        // Send authentication immediately upon connection if token is available
        // This ensures the server receives authentication as soon as possible
        if (token && ws.readyState === WebSocket.OPEN) {
          try {
            const authMessage = {
              type: "authenticate",
              token: stripAuthScheme(token)
            };

            createLogger.log(
              "📤 Sending authentication message immediately upon connection...",
              stateRefs.recordingStartTime.current
            );

            ws.send(JSON.stringify(authMessage));
            authSentRef.current = true; // Mark that authentication was sent
            createLogger.log(
              "✅ Authentication message sent immediately",
              stateRefs.recordingStartTime.current
            );
          } catch (err) {
            createLogger.error(
              "⚠️ Error sending immediate authentication:",
              err,
              stateRefs.recordingStartTime.current
            );
            // Don't reject here - let the normal authentication flow handle it
          }
        }

        resolve(ws);
      };

      ws.onerror = (error) => {
        clearConnectionTimeout();
        if (!settled) {
          if (wsRefs.ws.current === ws) {
            wsRefs.ws.current = null;
          }
          reject(error);
        } else {
          createLogger.error(
            "❌ WebSocket error:",
            error,
            stateRefs.recordingStartTime.current
          );
        }
      };

      ws.onclose = (event) => {
        clearConnectionTimeout();
        createLogger.log(
          `🔒 WebSocket closed (code: ${event.code}, reason: ${event.reason})`,
          stateRefs.recordingStartTime.current
        );
        if (!settled && wsRefs.ws.current === ws) {
          wsRefs.ws.current = null;
        }

        // Handle reconnection logic
        // Don't reconnect if we're in the middle of stopping/pausing
        if (
          stateRefs.isRecording.current &&
          event.code !== 1000 &&
          !stateRefs.isStopping.current &&
          !stateRefs.isPausing.current
        ) {
          if (wsRefs.reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
            createLogger.error(
              `❌ Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Transitioning to paused state.`,
              undefined,
              stateRefs.recordingStartTime.current
            );

            stateRefs.isRecording.current = false;
            // M5: resumeRecording clears pauseStartTime before connecting. On
            // reconnect failure we re-enter paused state, so restart the pause
            // timer so calculateDuration doesn't count this time as active.
            stateRefs.pauseStartTime.current = Date.now();
            dispatch(setStreamingReconnecting(false));

            // Use finalizePauseRef to get idempotency, cross-tab broadcast, and
            // session/favicon update in one place. skipWsClose because the WS is
            // already closed (we are inside onclose).
            const doFinalize = finalizePauseRef?.current;
            if (doFinalize) {
              void doFinalize({
                skipWsClose: true,
                showError: false, // toast is sent below, unconditionally for both branches
                reason: "max_reconnects"
              }).catch((err: unknown) => {
                createLogger.error(
                  "❌ Error in finalizePause after max reconnect attempts:",
                  err,
                  stateRefs.recordingStartTime.current
                );
              });
            } else {
              // Fallback when finalizePauseRef is not yet wired (should not happen in practice)
              dispatch(setStreamingPaused(true));
              dispatch(setStreamingAuthenticated(false));
              dispatch(pauseScribeRecording());
              void (async () => {
                try {
                  await updateRecordingSessionState(RECORDING_STATE_PAUSED);
                  setFavicon(FAVICON_PATHS.PAUSED);
                } catch (err) {
                  createLogger.error(
                    "❌ Error updating session state after connection failure:",
                    err,
                    stateRefs.recordingStartTime.current
                  );
                }
              })();
            }

            dispatch(
              setCustomMessage(ERROR_MESSAGES.RECORDING_CONNECTION_FAILED)
            );
            dispatch(setOpen(10000));

            createLogger.log(
              "✅ Transitioned to paused state after connection failure. User can click Resume to retry.",
              stateRefs.recordingStartTime.current
            );

            return;
          }

          wsRefs.reconnectAttempts.current += 1;
          const currentDelay = wsRefs.reconnectDelay.current;
          createLogger.log(
            `⚠️ Unexpected disconnect during recording. Attempting reconnection ${wsRefs.reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS} in ${currentDelay}ms...`,
            stateRefs.recordingStartTime.current
          );

          if (wsRefs.reconnectTimeout.current) {
            clearTimeout(wsRefs.reconnectTimeout.current);
          }

          wsRefs.reconnectDelay.current = Math.min(
            wsRefs.reconnectDelay.current * 2,
            30000
          );

          dispatch(setStreamingReconnecting(true));

          wsRefs.reconnectTimeout.current = setTimeout(async () => {
            // Clear the timeout ref immediately since we're now executing
            wsRefs.reconnectTimeout.current = null;

            if (
              shouldSkipAutoReconnect({
                resumeInFlight: resumeInFlightRef?.current ?? false,
                isConnecting: stateRefs.isConnecting.current,
                isStopping: stateRefs.isStopping.current,
                isPausing: stateRefs.isPausing.current
              })
            ) {
              createLogger.log(
                "⏭️ Skipping auto-reconnect — resume or connect already in progress",
                stateRefs.recordingStartTime.current
              );
              return;
            }

            if (
              stateRefs.isRecording.current &&
              stateRefs.scribeId.current &&
              wsRefs.reconnectAttempts.current <= MAX_RECONNECT_ATTEMPTS &&
              stateRefs.recordingParams.current
            ) {
              createLogger.log(
                `🔄 Attempting to reconnect and resume recording (attempt ${wsRefs.reconnectAttempts.current})...`,
                stateRefs.recordingStartTime.current
              );

              try {
                const startRecordingFn = startRecordingInternalRef.current;
                if (startRecordingFn) {
                  await startRecordingFn(
                    stateRefs.recordingParams.current.patientId,
                    stateRefs.recordingParams.current.chartNoteId,
                    { isReconnection: true, suppressSavingUi: true }
                  );
                }
              } catch (error) {
                createLogger.error(
                  "❌ Reconnection attempt failed:",
                  error,
                  stateRefs.recordingStartTime.current
                );
              }
            } else if (!stateRefs.isRecording.current) {
              wsRefs.reconnectAttempts.current = 0;
              wsRefs.reconnectDelay.current = 2000;
            }
          }, currentDelay);
        }
      };

      // Handle messages from backend
      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer || event.data instanceof Blob) {
          createLogger.warn(
            "⚠️ Received binary data from backend (unexpected)",
            stateRefs.recordingStartTime.current
          );
          return;
        }

        try {
          const msg = JSON.parse(event.data);

          if (msg.type === "authenticated") {
            createLogger.log(
              "✅ Authentication successful",
              stateRefs.recordingStartTime.current
            );
            dispatch(setStreamingAuthenticated(true));
            dispatch(setStreamingError(undefined));
            authSucceededRef.current = true; // Mark as succeeded

            // If authenticate() is waiting, resolve it now
            if (authResolverRef.current) {
              const resolver = authResolverRef.current;
              authResolverRef.current = null; // Clear to prevent double resolution
              resolver.resolve();
            }
          } else if (msg.type === "recording_started") {
            const resumed = Boolean(msg.resumed);
            const sessionNumber =
              typeof msg.session_number === "number" && msg.session_number > 0
                ? msg.session_number
                : 1;
            const logPrefix = resumed
              ? "♻️ Resumed recording"
              : "✅ Recording started";
            createLogger.log(
              `${logPrefix}, scribe_id: ${msg.scribe_id}, session: ${sessionNumber}`,
              stateRefs.recordingStartTime.current
            );
            wsRefs.reconnectAttempts.current = 0;
            wsRefs.reconnectDelay.current = 2000;

            // C4: stop may have fired while we were awaiting recording_started.
            // Capture scribeId so the in-flight stop can send end_recording, but
            // skip all state updates that would resurrect the UI into "recording".
            if (stateRefs.isStopping.current) {
              if (typeof msg.scribe_id === "number" && msg.scribe_id > 0) {
                stateRefs.scribeId.current = msg.scribe_id;
                dispatch(setStreamingScribeId(msg.scribe_id));
              }
              createLogger.warn(
                "⚠️ Ignoring recording_started: stop in progress",
                stateRefs.recordingStartTime.current
              );
              return;
            }

            stateRefs.isRecording.current = true;

            // Set mediaRecorderRef to signal recording is active
            mediaRecorderRef.current = {} as MediaRecorder;

            if (typeof msg.scribe_id === "number" && msg.scribe_id > 0) {
              dispatch(setStreamingScribeId(msg.scribe_id));
              stateRefs.scribeId.current = msg.scribe_id;
            }

            dispatch(setStreamingConnecting(false));
            dispatch(setStreamingReconnecting(false));
            dispatch(setStreamingPaused(false));
            dispatch(setStreamingError(undefined));
            dispatch(setStreamingWasResumed(resumed));

            if (!resumed) {
              finalTranscriptEntriesRef.current = [];
            }

            // Dispatch the appropriate recording state based on whether it's a resume
            if (resumed) {
              dispatch(resumeScribeRecording());
              createLogger.log(
                "✅ Recording resumed successfully",
                stateRefs.recordingStartTime.current
              );
            } else {
              // Get recording params from stateRefs
              const params = stateRefs.recordingParams.current;
              dispatch(
                startScribeRecording({
                  patientId: params?.patientId,
                  chartNoteId: params?.chartNoteId
                })
              );
              createLogger.log(
                "✅ Recording started successfully",
                stateRefs.recordingStartTime.current
              );
            }

            // Start volume analysis now that recording is confirmed
            startVolumeAnalysis();
            createLogger.log(
              "🔊 Volume analysis started",
              stateRefs.recordingStartTime.current
            );

            // FE-5: watch for a phantom session — backend confirmed the start, so
            // audio frames must follow shortly. If none are sent within the window
            // (the resume bug's signature), emit a high-signal warning.
            armPhantomSessionWatchdog();
            dispatch(setStreamingSessionNumber(sessionNumber));
            if (!resumed || !stateRefs.recordingStartTime.current) {
              stateRefs.recordingStartTime.current = Date.now();
            }

            // Start audio worker recording and chunk saving
            // IMPORTANT: This runs asynchronously but we handle errors properly
            (async () => {
              try {
                if (audioWorkerRef.current) {
                  const params = stateRefs.recordingParams.current;

                  // Use existing recording ID if resuming, otherwise start new
                  let recordingId: string;
                  if (resumed && audioRecordingIdRef.current) {
                    // Resume existing recording - don't create a new one!
                    recordingId = audioRecordingIdRef.current;
                    // Pass recordingId to worker so it knows which recording to save chunks to
                    await audioWorkerRef.current.resumeRecording(recordingId);
                    createLogger.log(
                      `🎙️ Audio worker recording RESUMED, recordingId: ${recordingId}, isStreaming: true`,
                      stateRefs.recordingStartTime.current
                    );
                  } else {
                    // Start new recording
                    recordingId = await audioWorkerRef.current.startRecording({
                      patientId: params?.patientId || 0,
                      userId: 0, // Not available in streaming context
                      patientFullName: undefined, // Not available in streaming context
                      isStreaming: true // Mark this as a streaming recording
                    });
                    // Store recording ID for future resume operations
                    audioRecordingIdRef.current = recordingId;
                    createLogger.log(
                      `🎙️ Audio worker recording STARTED, recordingId: ${recordingId}, isStreaming: true`,
                      stateRefs.recordingStartTime.current
                    );
                  }

                  // Start chunk saving
                  startChunkSaving();
                  createLogger.log(
                    "💾 Chunk saving started",
                    stateRefs.recordingStartTime.current
                  );

                  // Update session with audioRecordingId for page reload recovery
                  try {
                    await updateRecordingSessionState(
                      RECORDING_STATE_IN_PROGRESS,
                      {
                        scribeId: msg.scribe_id,
                        audioRecordingId: recordingId
                      }
                    );
                    createLogger.log(
                      `✅ Session updated with scribe_id: ${msg.scribe_id}, audioRecordingId: ${recordingId}`,
                      stateRefs.recordingStartTime.current
                    );
                  } catch (error_) {
                    createLogger.error(
                      "❌ Error updating session with scribe_id and audioRecordingId:",
                      error_,
                      stateRefs.recordingStartTime.current
                    );
                    // Don't fail the recording if session update fails - it's not critical
                  }

                  // IMPORTANT: Link the recording to the backend scribe_id immediately
                  // This allows handleDownloadLocalAudio to find the recording even when status = 0
                  if (msg.scribe_id && recordingId) {
                    try {
                      const { getDB } = await import(
                        "../../medical-scribe-db/medical-scribe-db.util"
                      );
                      const db = await getDB();
                      const tx = db.transaction("recordings", "readwrite");
                      const store = tx.objectStore("recordings");
                      const recording = await store.get(recordingId);

                      if (recording) {
                        recording.medical_transcribe_id = msg.scribe_id;
                        await store.put(recording);
                        await tx.done;
                        createLogger.log(
                          `✅ Linked audio recording ${recordingId} to scribe ID ${msg.scribe_id}`,
                          stateRefs.recordingStartTime.current
                        );
                      } else {
                        createLogger.warn(
                          `⚠️ Recording ${recordingId} not found in IndexedDB when trying to link scribe ID ${msg.scribe_id}`,
                          stateRefs.recordingStartTime.current
                        );
                      }
                    } catch (err) {
                      createLogger.error(
                        "❌ Error linking audio recording to scribe ID (immediate link):",
                        err,
                        stateRefs.recordingStartTime.current
                      );
                      // Don't fail the recording if linking fails - it will be retried on stop
                    }
                  }
                }
              } catch (err) {
                createLogger.error(
                  "❌ Error starting audio worker recording:",
                  err,
                  stateRefs.recordingStartTime.current
                );

                // If audio worker fails, we need to notify the user
                // The recording state is inconsistent - backend thinks we're recording
                // but we can't save audio chunks
                dispatch(
                  setStreamingError(
                    "Failed to initialize audio recording. Please stop and try again."
                  )
                );
                dispatch(
                  setCustomMessage(
                    "Audio recording initialization failed. Recording may not be saved properly."
                  )
                );
                dispatch(setOpen(10000));
              }
            })().catch((err) => {
              // Catch any unhandled promise rejections
              createLogger.error(
                "❌ Unhandled error in audio worker initialization:",
                err,
                stateRefs.recordingStartTime.current
              );
            });
          } else if (msg.type === "recording_paused") {
            createLogger.log(
              "✅ Recording paused by backend",
              stateRefs.recordingStartTime.current
            );
            stateRefs.isRecording.current = false;
            const finalizePause = finalizePauseRef?.current;
            if (finalizePause) {
              void finalizePause().catch((err: unknown) => {
                createLogger.error(
                  "❌ Error in finalizePause from recording_paused handler:",
                  err,
                  stateRefs.recordingStartTime.current
                );
              });
            } else {
              dispatch(setStreamingAuthenticated(false));
              dispatch(setStreamingReconnecting(false));
              dispatch(setStreamingPaused(true));
              dispatch(pauseScribeRecording());
              setFavicon(FAVICON_PATHS.PAUSED);
              const closeWs = closeWebSocketRef?.current;
              if (closeWs) {
                void closeWs("Paused by user").catch((err: unknown) => {
                  createLogger.error(
                    "❌ Error closing WebSocket from recording_paused fallback:",
                    err,
                    stateRefs.recordingStartTime.current
                  );
                });
              }
            }
          } else if (msg.type === "recording_ended") {
            createLogger.log(
              "✅ Recording ended",
              stateRefs.recordingStartTime.current
            );
            // Clear scribeId when recording ends to prevent incorrect reconnection detection
            dispatch(setStreamingScribeId(undefined));
            dispatch(setStreamingConnecting(false));
            dispatch(setStreamingWasResumed(false));
            dispatch(setStreamingSessionNumber(undefined));
          } else if (msg.type === "transcription_reconnected") {
            createLogger.log(
              "✅ Transcription service reconnected",
              stateRefs.recordingStartTime.current
            );
            wsRefs.reconnectAttempts.current = 0;
            wsRefs.reconnectDelay.current = 2000;
            dispatch(setStreamingReconnecting(false));
            dispatch(setStreamingError(undefined));
          } else if (msg.type === "transcription_error") {
            // Log transcription error with context
            const errorContext = {
              errorType: "transcription_error",
              errorMessage: msg.error || "Unknown transcription error",
              scribeId: stateRefs.scribeId.current,
              recordingState: stateRefs.isRecording.current
                ? "recording"
                : "not_recording"
            };

            createLogger.error(
              `❌ Transcription error: ${msg.error || "Unknown error"}`,
              errorContext,
              stateRefs.recordingStartTime.current
            );
            dispatch(
              setStreamingError(msg.error || "Transcription service error")
            );
            dispatch(setStreamingReconnecting(false));
            dispatch(setStreamingConnecting(false));

            // Only show the error banner while recording is active.
            // A transcription_error that arrives after end_recording (state already
            // cleared) is a normal server-side close artifact, not a real failure.
            if (stateRefs.isRecording.current || stateRefs.isPausing.current) {
              dispatch(
                setCustomMessage(ERROR_MESSAGES.TRANSCRIPTION_SERVICE_ERROR)
              );
              dispatch(setOpen(10000));
            }
          } else if (msg.type === "recording_error") {
            // Log backend error with full context for debugging
            const errorContext = {
              errorType: "backend_recording_error",
              errorMessage: msg.error || "Unknown error",
              reason: msg.reason,
              hasScribeId: Boolean(stateRefs.scribeId.current),
              hasRecordingParams: Boolean(stateRefs.recordingParams.current),
              recordingState: stateRefs.isRecording.current
                ? "recording"
                : "not_recording"
            };

            createLogger.error(
              `❌ Recording error from backend: ${msg.error || "Unknown error"}`,
              errorContext,
              stateRefs.recordingStartTime.current
            );

            // If we have recordingParams (we're in the middle of a recording attempt),
            // transition to paused state so user can retry
            const hasRecordingParams = Boolean(
              stateRefs.recordingParams.current
            );

            if (hasRecordingParams) {
              createLogger.log(
                "⚠️ Backend error during recording attempt, transitioning to paused state. User can click Resume to retry.",
                stateRefs.recordingStartTime.current
              );

              stateRefs.isRecording.current = false;
              // M5: re-entering paused state after an error — restart pause timer
              // so calculateDuration doesn't count subsequent paused time as active.
              stateRefs.pauseStartTime.current = Date.now();
              dispatch(
                setStreamingError(
                  msg.error || "Recording service error. Please try resuming."
                )
              );
              dispatch(setStreamingConnecting(false));
              dispatch(setStreamingReconnecting(false));
              dispatch(setStreamingPaused(true));
              dispatch(pauseScribeRecording());

              // Show user notification
              dispatch(
                setCustomMessage(ERROR_MESSAGES.RECORDING_SERVICE_ERROR)
              );
              dispatch(setOpen(10000));

              // Update session state to paused and save with recording params
              void (async () => {
                try {
                  // If we have a scribeId, update session, otherwise create new session in paused state
                  if (stateRefs.scribeId.current) {
                    await updateRecordingSessionState(RECORDING_STATE_PAUSED);
                  } else {
                    // Create session for future retry (even without scribeId yet)
                    const { saveRecordingSession } = await import(
                      "../../recording-session.util"
                    );
                    await saveRecordingSession({
                      recordingId: `streaming_recording_${Date.now()}`,
                      patientId:
                        stateRefs.recordingParams.current!.patientId || 0,
                      patientFullName: undefined,
                      chartNoteId:
                        stateRefs.recordingParams.current!.chartNoteId,
                      recordingState: RECORDING_STATE_PAUSED,
                      startTime: Date.now(),
                      tabInstanceId: (
                        await import("hooks/use-sync-between-tabs.hook")
                      ).appInstanceId,
                      scribeId: undefined
                    });
                  }
                  setFavicon(FAVICON_PATHS.PAUSED);

                  createLogger.log(
                    "✅ Session saved in paused state, user can retry",
                    stateRefs.recordingStartTime.current
                  );
                } catch (err) {
                  createLogger.error(
                    "❌ Error saving session after recording error:",
                    err,
                    stateRefs.recordingStartTime.current
                  );
                }
              })();

              // Close WebSocket but keep params for retry
              if (wsRefs.ws.current) {
                try {
                  if (wsRefs.ws.current.readyState === WebSocket.OPEN) {
                    wsRefs.ws.current.close(1000, "Backend error");
                  }
                } catch (err) {
                  createLogger.error(
                    "⚠️ Error closing WebSocket after backend error",
                    err,
                    stateRefs.recordingStartTime.current
                  );
                }
                wsRefs.ws.current = null;
              }
            } else {
              // No recording params, this shouldn't happen but handle gracefully
              createLogger.log(
                "❌ Backend error with no recording params, cleaning up",
                stateRefs.recordingStartTime.current
              );

              stateRefs.isRecording.current = false;
              dispatch(setStreamingError(msg.error || "Recording error"));
              dispatch(setStreamingConnecting(false));
              dispatch(setStreamingReconnecting(false));
              dispatch(setStreamingWasResumed(false));
              dispatch(setStreamingSessionNumber(undefined));

              // Properly await cleanup to ensure resources are released
              (async () => {
                try {
                  await cleanupResources();
                } catch (error_) {
                  createLogger.error(
                    "❌ Error during cleanup after backend error:",
                    error_,
                    stateRefs.recordingStartTime.current
                  );
                }
              })().catch((error_) => {
                createLogger.error(
                  "❌ Unhandled error in cleanup after backend error:",
                  error_,
                  stateRefs.recordingStartTime.current
                );
              });
            }
          } else if (msg.type === "resume_snapshot") {
            createLogger.log(
              "📦 Resume snapshot received from backend",
              stateRefs.recordingStartTime.current
            );
            const entries = mapChunksToTranscriptEntries(msg.chunks || []);
            if (entries.length) {
              finalTranscriptEntriesRef.current = entries;
              dispatch(setStreamingTranscript(entries));
              lastInterimRef.current = null;
            }
          } else if (msg.type === "silence_detected") {
            // Only show dialog if we're actively recording
            if (stateRefs.isRecording.current) {
              createLogger.log(
                "🔇 Silence detected, showing warning dialog",
                stateRefs.recordingStartTime.current
              );
              dispatch(setShowSilenceWarningDialog(true));
            }
          } else if (msg.type === "Results") {
            handleTranscriptionMessage(msg);
          } else if (msg.type === "TurnInfo") {
            const transcriptText = msg.transcript || "";
            const words: Array<{ word?: string }> = Array.isArray(msg.words)
              ? msg.words
              : [];

            if (transcriptText && transcriptText.trim()) {
              const normalized = {
                type: "Results",
                is_final: true,
                channel: {
                  alternatives: [
                    {
                      transcript: transcriptText,
                      words: words.map((w) => ({
                        word: w?.word,
                        speaker: 0
                      })),
                      confidence: msg.end_of_turn_confidence ?? undefined
                    }
                  ]
                }
              };

              handleTranscriptionMessage(normalized);
            }
          } else if (msg.error) {
            // Log generic backend error with context
            const errorContext = {
              errorType: "backend_error",
              messageType: msg.type || "unknown",
              errorMessage: msg.error,
              scribeId: stateRefs.scribeId.current,
              recordingState: stateRefs.isRecording.current
                ? "recording"
                : "not_recording"
            };

            createLogger.error(
              `❌ Backend error (${msg.type || "unknown"}): ${msg.error}`,
              errorContext,
              stateRefs.recordingStartTime.current
            );
            dispatch(setStreamingError(msg.error));
            dispatch(setStreamingConnecting(false));
            dispatch(setStreamingAuthenticated(false));
          }
        } catch (err) {
          createLogger.error(
            "❌ Error parsing message:",
            err,
            stateRefs.recordingStartTime.current
          );
        }
      };
    });
  }, [
    dispatch,
    handleTranscriptionMessage,
    cleanupResources,
    wsRefs,
    stateRefs,
    lastInterimRef,
    mediaRecorderRef,
    startRecordingInternalRef,
    startVolumeAnalysis,
    armPhantomSessionWatchdog,
    audioWorkerRef,
    audioRecordingIdRef,
    startChunkSaving,
    token,
    authSentRef,
    closeWebSocketRef,
    finalizePauseRef,
    resumeInFlightRef
  ]);

  /**
   * Connect to WebSocket with retry logic
   */
  const connectWebSocket = useCallback(async (): Promise<WebSocket> => {
    await ensureWebSocketClosed();

    let lastError: unknown = null;
    for (let attempt = 1; attempt <= MAX_WS_CONNECT_ATTEMPTS; attempt++) {
      try {
        if (attempt > 1) {
          createLogger.log(
            `🔁 Retrying WebSocket connection (attempt ${attempt}/${MAX_WS_CONNECT_ATTEMPTS})`,
            stateRefs.recordingStartTime.current
          );
        }
        const socket = await createWebSocketConnection();
        return socket;
      } catch (error) {
        lastError = error;
        createLogger.error(
          `❌ WebSocket connection attempt ${attempt} failed`,
          error,
          stateRefs.recordingStartTime.current
        );
        if (attempt < MAX_WS_CONNECT_ATTEMPTS) {
          await wait(WS_RETRY_BASE_DELAY_MS * attempt);
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error("Failed to establish WebSocket connection");
  }, [
    createWebSocketConnection,
    ensureWebSocketClosed,
    stateRefs.recordingStartTime
  ]);

  /**
   * Authenticate with backend
   */
  const authenticate = useCallback(
    async (ws: WebSocket): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (ws.readyState !== WebSocket.OPEN) {
          reject(new Error("WebSocket is not open. Cannot authenticate."));
          return;
        }

        if (!token) {
          reject(
            new Error(
              "Authentication token is required. Please ensure you are logged in."
            )
          );
          return;
        }

        // Store resolver FIRST so main handler can resolve if response comes during setup
        authResolverRef.current = { resolve, reject };

        // Check if authentication already succeeded (response came before this function was called)
        // This check must happen AFTER storing resolver to handle race conditions
        if (authSucceededRef.current && authSentRef.current) {
          createLogger.log(
            "✅ Authentication already succeeded, resolving immediately",
            stateRefs.recordingStartTime.current
          );
          // Clear resolver since we're resolving here
          authResolverRef.current = null;
          resolve();
          return;
        }

        let resolved = false;
        let cleaned = false;

        const cleanup = () => {
          // Guard against double cleanup
          if (cleaned) return;
          cleaned = true;

          clearTimeout(timeout);
          // Clear resolver if we're cleaning up
          if (
            authResolverRef.current &&
            authResolverRef.current.resolve === resolve
          ) {
            authResolverRef.current = null;
          }
          if (originalOnMessage) {
            ws.onmessage = originalOnMessage;
          }
          if (originalOnClose) {
            ws.onclose = originalOnClose;
          }
        };

        const originalOnMessage = ws.onmessage;
        const originalOnClose = ws.onclose;

        const timeout = setTimeout(() => {
          cleanup();
          if (!resolved) {
            resolved = true;
            // Clear resolver on timeout since we're rejecting
            if (
              authResolverRef.current &&
              authResolverRef.current.resolve === resolve
            ) {
              authResolverRef.current = null;
            }
            reject(
              new Error("Authentication timeout - no response from server")
            );
          }
        }, 5000);

        ws.onclose = (event) => {
          if (!resolved) {
            cleanup();
            resolved = true;
            // Clear resolver on close since we're rejecting
            if (
              authResolverRef.current &&
              authResolverRef.current.resolve === resolve
            ) {
              authResolverRef.current = null;
            }
            reject(
              new Error(
                `Connection closed during authentication (code: ${event.code}, reason: ${event.reason || "Unknown"})`
              )
            );
          }
          if (originalOnClose) {
            originalOnClose.call(ws, event);
          }
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);

            if (msg.type === "authenticated") {
              if (!resolved) {
                cleanup();
                resolved = true;
                createLogger.log(
                  "✅ Authentication successful",
                  stateRefs.recordingStartTime.current
                );
                // Dispatch Redux actions to update UI state
                dispatch(setStreamingAuthenticated(true));
                dispatch(setStreamingError(undefined));
                authSucceededRef.current = true; // Mark as succeeded
                // Clear resolver since we're resolving here
                if (
                  authResolverRef.current &&
                  authResolverRef.current.resolve === resolve
                ) {
                  authResolverRef.current = null;
                }
                resolve();
              }
            } else if (msg.error && msg.type !== "Results") {
              if (!resolved) {
                cleanup();
                resolved = true;
                // Clear resolver on error since we're rejecting
                if (
                  authResolverRef.current &&
                  authResolverRef.current.resolve === resolve
                ) {
                  authResolverRef.current = null;
                }
                reject(new Error(msg.error || "Authentication failed"));
              }
            } else if (originalOnMessage) {
              originalOnMessage.call(ws, event);
            }
          } catch (err) {
            createLogger.error(
              `❌ Error parsing auth response: ${err instanceof Error ? err.message : String(err)}`,
              err,
              stateRefs.recordingStartTime.current
            );
            if (originalOnMessage) {
              originalOnMessage.call(ws, event);
            }
          }
        };

        try {
          // Check if authentication was already sent (e.g., in onopen handler)
          // If so, we just need to wait for the response without sending again
          if (authSentRef.current) {
            createLogger.log(
              "⏳ Authentication already sent, waiting for response...",
              stateRefs.recordingStartTime.current
            );
            // Don't send again, just wait for the response
          } else {
            const authMessage = {
              type: "authenticate",
              token: token
            };

            // Only send if WebSocket is still open
            if (ws.readyState === WebSocket.OPEN) {
              createLogger.log(
                "📤 Sending authentication message...",
                stateRefs.recordingStartTime.current
              );

              ws.send(JSON.stringify(authMessage));
              authSentRef.current = true;
              createLogger.log(
                "✅ Authentication message sent successfully",
                stateRefs.recordingStartTime.current
              );
            } else {
              createLogger.warn(
                `⚠️ WebSocket not open when trying to authenticate (state: ${ws.readyState})`,
                stateRefs.recordingStartTime.current
              );
              throw new Error(`WebSocket not open (state: ${ws.readyState})`);
            }
          }
        } catch (err) {
          cleanup();
          if (!resolved) {
            resolved = true;
            reject(
              new Error(
                `Failed to send authentication message: ${
                  err instanceof Error ? err.message : "Unknown error"
                }`
              )
            );
          }
        }
      });
    },
    [token, stateRefs, authSentRef, authSucceededRef, authResolverRef, dispatch]
  );

  /**
   * Send continue_recording message to backend (when user dismisses silence warning)
   */
  const sendContinueRecording = useCallback(() => {
    const ws = wsRefs.ws.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        const fingerprint = microphoneFingerprintRef.current;
        const continueMessage: {
          type: string;
          microphone?: MicrophoneInput;
        } = { type: "continue_recording" };
        if (fingerprint) {
          continueMessage.microphone = fingerprint;
        }
        ws.send(JSON.stringify(continueMessage));
        createLogger.log(
          "📤 Sent continue_recording message",
          stateRefs.recordingStartTime.current
        );
      } catch (err) {
        createLogger.error(
          "❌ Error sending continue_recording message:",
          err,
          stateRefs.recordingStartTime.current
        );
      }
    }
  }, [wsRefs, stateRefs, microphoneFingerprintRef]);

  return {
    connectWebSocket,
    authenticate,
    sendContinueRecording
  };
};
